/**
 * contentBlockForms — shared content-block config sub-form (Story 37.4).
 *
 * The single source of truth for the report editor's page-element (content
 * block) config controls. LIFTED (moved, not copied) out of
 * `WidgetConfigPanel.tsx` — where the four content-block configs lived as INLINE
 * `{widget.type === "…"}` JSX conditionals — so that BOTH the widget config
 * panel (editing an existing content block) AND the Phase-2 content-blocks
 * category picker (`ContentBlocksCategoryPanel`, configure-then-add) consume one
 * definition and cannot drift apart. (Phase-2 anti-drift mandate / AC #2 / AC #8.)
 *
 * LAYOUT REGISTER (2026-06-13 restyle): these fields follow the widget editor's
 * §10 register exactly — `grid grid-cols-[1fr_130px] items-center gap-2` rows
 * (label left at text-[13px], control in the 130px column), the editor's
 * segmented control for alignment (h-9, p-1, L/C/R letter buttons, selected =
 * bg-foreground/10), and §10.1 group dividers (`border-t-[0.5px] border-input/70
 * pt-4 mt-2` — NEVER bare `border-t`, which renders 1px). Multi-line textareas
 * are the one sanctioned deviation: they stay stacked full-width (a textarea in
 * a 130px column is unusable); this matches the annotations editor precedent.
 *
 * The `content-blocks` category has EXACTLY FOUR types: `heading`, `paragraph`,
 * `rich-text`, `spacer-divider`. (`text-block` is NOT a content block — it lives
 * in the `integrations-metrics` category; do NOT add it here.)
 *
 * Contract — UNCHANGED from the original (do NOT refactor to `{config,onChange}`,
 * do NOT add a `mode` prop): the sub-form takes
 * `{ widget: WidgetConfig; onUpdate: (id, changes) => void }` and reads/writes
 * `widget.config` via a local `handleConfigChange(key, value)` that is byte-for-
 * byte identical to WidgetConfigPanel's original. The config panel passes the
 * real selected widget; the category panel passes a synthetic draft-widget + a
 * draft `onUpdate` adapter (see ContentBlocksCategoryPanel).
 *
 * Content blocks render PLAINTEXT (the four renderers emit `content` as a text
 * node — no `dangerouslySetInnerHTML`, no markdown/HTML parser, no sanitizer).
 * `rich-text` is a *styled-plaintext variant* (body/callout/quote), NOT an HTML
 * editor despite its name. This sub-form introduces NO HTML rendering and NO
 * sanitizer (AC #9 / Red Team).
 *
 * LEAF MODULE (Pre-mortem E): this file must NOT import from
 * `WidgetConfigPanel.tsx` / `WidgetPalette.tsx` / `CanvasEditor.tsx`. Its only
 * deps are `WidgetConfig` (lib/templateUtils) + the §10.3 `SelectField`
 * primitive. No import cycle.
 *
 * Originally Story 5.24; moved here verbatim by Story 37.4; restyled to the
 * editor register 2026-06-13 (config read/write behavior unchanged).
 */

import type { WidgetConfig } from "../../lib/templateUtils";
import { SelectField } from "./SelectField";

/** The four `content-blocks` category types (NOT `text-block` — that's elsewhere). */
export type ContentBlockType = "heading" | "paragraph" | "rich-text" | "spacer-divider";

/** §10.1 field row: label left (flex), control fixed at 130px. */
function FieldRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[1fr_130px] items-center gap-2">
      <label htmlFor={htmlFor} className="min-w-0 text-[13px] text-foreground">
        {label}
      </label>
      <div className="w-full">{children}</div>
    </div>
  );
}

/**
 * The editor's segmented alignment control (§10.2/§10.4 — same markup as the
 * WidgetConfigPanel "Title align" field): h-9 bordered track, letter buttons,
 * selected = bg-foreground/10. `testIdPrefix` preserves the original
 * `{type}-align-{value}` testids the tests target.
 */
function AlignSegmented({
  options,
  value,
  testIdPrefix,
  onChange,
}: {
  options: readonly string[];
  value: string;
  testIdPrefix: string;
  onChange: (align: string) => void;
}) {
  return (
    <div
      className="flex h-9 w-full items-center gap-1 rounded-lg border-[0.5px] border-input p-1 shadow-xs"
      role="group"
      aria-label="Alignment"
    >
      {options.map((a) => (
        <button
          key={a}
          type="button"
          aria-label={`Align ${a}`}
          data-testid={`${testIdPrefix}-${a}`}
          aria-pressed={value === a}
          onClick={() => onChange(a)}
          className={`flex h-full flex-1 touch-target-sm items-center justify-center rounded-md text-[12px] leading-none cursor-pointer ${
            value === a
              ? "bg-foreground/10 text-foreground"
              : "text-muted-foreground hover:bg-accent/50"
          }`}
        >
          {a.charAt(0).toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/**
 * The shared content-block config controls. Branches internally on
 * `widget.type` and renders the matching fields; renders nothing for any
 * non-content-block type, so it can be dropped into `WidgetConfigPanel`'s render
 * tree exactly where the four inline blocks were.
 *
 * Consumed by:
 *  - `WidgetConfigPanel` (the four inline blocks were replaced with a single
 *    `<ContentBlockConfigSection widget onUpdate />`), and
 *  - `ContentBlocksCategoryPanel` (configure-then-add, via a synthetic draft-widget).
 */
export function ContentBlockConfigSection({
  widget,
  onUpdate,
}: {
  widget: WidgetConfig;
  onUpdate: (widgetId: string, changes: Partial<WidgetConfig>) => void;
}) {
  // Identical to WidgetConfigPanel's original local handler — a MOVE of behavior,
  // not a redesign. Reads/writes via the same shallow config-merge patch.
  const handleConfigChange = (key: string, value: string | number) => {
    onUpdate(widget.id, {
      config: { ...(widget.config as Record<string, unknown>), [key]: value },
    });
  };

  const config = widget.config as Record<string, unknown>;

  return (
    <>
      {/* Heading Config (Story 5.24; editor-register restyle 2026-06-13) */}
      {widget.type === "heading" && (
        <div
          className="space-y-3 border-t-[0.5px] border-input/70 pt-4 mt-2"
          data-testid="heading-config"
        >
          <label className="block text-[13.5px] font-medium">Heading</label>

          <FieldRow label="Content" htmlFor="heading-content">
            <input
              id="heading-content"
              data-testid="heading-content-input"
              type="text"
              value={(config.content as string) ?? ""}
              onChange={(e) => handleConfigChange("content", e.target.value)}
              placeholder="Add heading..."
              className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2.5 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
            />
          </FieldRow>

          <FieldRow label="Size">
            <SelectField
              ariaLabel="Size"
              testId="heading-size-select"
              value={(config.size as string) ?? "h2"}
              onChange={(v) => handleConfigChange("size", v)}
              triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
              options={[
                { value: "h1", label: "Heading 1 (Large)" },
                { value: "h2", label: "Heading 2 (Medium)" },
                { value: "h3", label: "Heading 3 (Small)" },
              ]}
            />
          </FieldRow>

          <FieldRow label="Alignment">
            <AlignSegmented
              options={["left", "center", "right"] as const}
              value={(config.align as string) ?? "left"}
              testIdPrefix="heading-align"
              onChange={(a) => handleConfigChange("align", a)}
            />
          </FieldRow>
        </div>
      )}

      {/* Paragraph Config (Story 5.24; editor-register restyle 2026-06-13) */}
      {widget.type === "paragraph" && (
        <div
          className="space-y-3 border-t-[0.5px] border-input/70 pt-4 mt-2"
          data-testid="paragraph-config"
        >
          <label className="block text-[13.5px] font-medium">Paragraph</label>

          {/* Multi-line content stays stacked full-width (sanctioned deviation —
              a textarea in the 130px column is unusable). */}
          <div>
            <label htmlFor="paragraph-content" className="text-[13px] text-foreground">
              Content
            </label>
            <textarea
              id="paragraph-content"
              data-testid="paragraph-content-input"
              rows={4}
              value={(config.content as string) ?? ""}
              onChange={(e) => handleConfigChange("content", e.target.value)}
              placeholder="Add paragraph text..."
              className="mt-1 w-full resize-none rounded-lg border-[0.5px] border-input bg-background px-2.5 py-2 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
            />
          </div>

          <FieldRow label="Alignment">
            <AlignSegmented
              options={["left", "center", "right", "justify"] as const}
              value={(config.align as string) ?? "left"}
              testIdPrefix="paragraph-align"
              onChange={(a) => handleConfigChange("align", a)}
            />
          </FieldRow>
        </div>
      )}

      {/* Rich Text Config (Story 5.24; editor-register restyle 2026-06-13) */}
      {widget.type === "rich-text" && (
        <div
          className="space-y-3 border-t-[0.5px] border-input/70 pt-4 mt-2"
          data-testid="rich-text-config"
        >
          <label className="block text-[13.5px] font-medium">Rich Text</label>

          {/* Multi-line content stays stacked full-width (see Paragraph note). */}
          <div>
            <label htmlFor="rich-text-content" className="text-[13px] text-foreground">
              Content
            </label>
            <textarea
              id="rich-text-content"
              data-testid="rich-text-content-input"
              rows={4}
              value={(config.content as string) ?? ""}
              onChange={(e) => handleConfigChange("content", e.target.value)}
              placeholder="Add content..."
              className="mt-1 w-full resize-none rounded-lg border-[0.5px] border-input bg-background px-2.5 py-2 text-[13.5px] shadow-xs focus:outline-none focus-visible:outline-none"
            />
          </div>

          <FieldRow label="Style variant">
            <SelectField
              ariaLabel="Style Variant"
              testId="rich-text-variant-select"
              value={(config.variant as string) ?? "body"}
              onChange={(v) => handleConfigChange("variant", v)}
              triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
              options={[
                { value: "body", label: "Body Text" },
                { value: "callout", label: "Callout" },
                { value: "quote", label: "Quote" },
              ]}
            />
          </FieldRow>
        </div>
      )}

      {/* Spacer/Divider Config (Story 5.24; editor-register restyle 2026-06-13) */}
      {widget.type === "spacer-divider" && (
        <div
          className="space-y-3 border-t-[0.5px] border-input/70 pt-4 mt-2"
          data-testid="spacer-divider-config"
        >
          <label className="block text-[13.5px] font-medium">Spacer / Divider</label>

          <FieldRow label="Style">
            <SelectField
              ariaLabel="Style"
              testId="spacer-style-select"
              value={(config.style as string) ?? "line"}
              onChange={(v) => handleConfigChange("style", v)}
              triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
              options={[
                { value: "spacing", label: "Empty Space" },
                { value: "line", label: "Solid Line" },
                { value: "dashed-line", label: "Dashed Line" },
              ]}
            />
          </FieldRow>

          <FieldRow label="Height">
            <SelectField
              ariaLabel="Height (rows)"
              testId="spacer-height-select"
              value={String((config.height as number) ?? 1)}
              onChange={(v) => handleConfigChange("height", parseInt(v, 10))}
              triggerClassName="h-9 w-full rounded-lg border-[0.5px] shadow-xs text-[13.5px]"
              options={[
                { value: "1", label: "1 Row" },
                { value: "2", label: "2 Rows" },
                { value: "3", label: "3 Rows" },
              ]}
            />
          </FieldRow>
        </div>
      )}
    </>
  );
}

/**
 * Per-type "is this draft non-blank / addable?" rule (AC #5). Used by the
 * `ContentBlocksCategoryPanel` Add gate and asserted by tests.
 *
 *  - heading / paragraph / rich-text: valid IFF `content` (trimmed) is non-empty
 *    — there is no code path from the panel to a blank text element.
 *  - spacer-divider: ALWAYS valid — its default `{ style: "line" }` already
 *    renders a non-blank divider; there is nothing to fill in.
 */
export function isContentBlockDraftValid(
  type: ContentBlockType,
  draft: Record<string, unknown>,
): boolean {
  if (type === "spacer-divider") return true;
  return typeof draft.content === "string" && draft.content.trim().length > 0;
}
